import React, {FC, createContext, useContext, useEffect, useState} from 'react'
import {WithChildren} from '../../helpers'
import {useLocation} from 'react-router-dom'
import {useAuth} from '../../../app/modules/auth'

export const getPageTitleFromRoute = (pathname: string, currentUser?: any): string => {
  const path = pathname.toLowerCase();
  if (path.includes('/dashboard')) return 'Dashboard';
  if (path.includes('/vouchers')) return 'Discounts';
  if (path.includes('/users/viewuser')) return 'Customers';
  if (path.includes('/users')) return 'Users';
  if (path.includes('/memberships')) return 'Memberships';
  if (path.includes('/locations')) return 'Locations';
  if (path.includes('/staff/viewstaff')) return currentUser?.role === 'super_admin' ? 'Staff' : 'Profile';
  if (path.includes('/staff')) return 'Staff';
  if (path.includes('/events')) return 'Programming';
  if (path.includes('/coaches/viewcoach')) return 'Coach';
  if (path.includes('/coaches')) return 'Coaches';
  if (path.includes('/bookings/addbooking')) return 'Booking';
  if (path.includes('/bookings')) return 'Bookings';
  return 'Dashboard';
}

export interface PageLink {
  title: string
  path: string
  isActive: boolean
  isSeparator?: boolean
}

export interface PageDataContextModel {
  pageTitle?: string
  pageDescription?: string
  setPageDescription: (_description: string) => void
  pageBreadcrumbs?: Array<PageLink>
  setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => void
}

const PageDataContext = createContext<PageDataContextModel>({
  setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => {},
  setPageDescription: (_description: string) => {},
})

const PageDataProvider: FC<WithChildren> = ({children}) => {
  const location = useLocation()
  const {currentUser} = useAuth()
  const pageTitle = getPageTitleFromRoute(location.pathname, currentUser)
  
  const [pageDescription, setPageDescription] = useState<string>('')
  const [pageBreadcrumbs, setPageBreadcrumbs] = useState<Array<PageLink>>([])
  
  const value: PageDataContextModel = {
    pageTitle,
    pageDescription,
    setPageDescription,
    pageBreadcrumbs,
    setPageBreadcrumbs,
  }
  
  return <PageDataContext.Provider value={value}>{children}</PageDataContext.Provider>
}

function usePageData() {
  return useContext(PageDataContext)
}

type Props = {
  description?: string
  breadcrumbs?: Array<PageLink>
}

const PageTitle: FC<Props & WithChildren> = ({description, breadcrumbs}) => {
  const {setPageDescription, setPageBreadcrumbs} = usePageData()

  useEffect(() => {
    if (description) {
      setPageDescription(description)
    }
    return () => {
      setPageDescription('')
    }
  }, [description])

  useEffect(() => {
    if (breadcrumbs) {
      setPageBreadcrumbs(breadcrumbs)
    }
    return () => {
      setPageBreadcrumbs([])
    }
  }, [breadcrumbs])

  return <></>
}

const PageDescription: FC<WithChildren> = ({children}) => {
  const {setPageDescription} = usePageData()
  useEffect(() => {
    if (children) {
      setPageDescription(children.toString())
    }
    return () => {
      setPageDescription('')
    }
  }, [children])
  return <></>
}

export {PageDescription, PageTitle, PageDataProvider, usePageData}